/**
 * Bravo Assistant — app-wide chat panel: a sparkle launcher opens a fixed right
 * panel of server-persisted conversations, with a Page Context chip that stamps
 * the current screen onto each user message.
 */
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu, MenuButton, MenuItems } from '@headlessui/react';
import {
  faXmark,
  faArrowUp,
  faSparkles,
  faPenToSquare,
  faChevronDown,
  faCheck,
  faLocationDot,
  faPen,
  faTrashCan
} from '@fortawesome/pro-light-svg-icons';
import type { ConversationSummary } from 'backend/src/ai/assistant-conversations';
import type { ChatMessage } from 'shared/src/api/ai-chat-types';
import type { PageContext } from 'shared/src/api/page-context-types';
import type { AssistantConversation } from 'backend/src/ai/assistant-conversations';
import { cn } from '@/lib/utils';
import { trpc } from '@/utils/trpc-client';
import { ChatWelcome } from './chat-welcome';
import { buildMessageSegments } from './message-segments';
import { useAssistantStore, type PendingAssistantSend } from './assistant-store';
import { useAssistantConversation } from './use-assistant-conversation';
import { useCurrentPageContext, hasPageContext } from './page-context';
import { useJiraSupportEnabled } from '@/utils/feature-flags';
import { usePanelResize } from './use-panel-resize';
import { VisualizationBlock } from './visualization/visualization-block';
import type { VisualizationBlockProps } from './visualization/visualization-block';
import { encodeVizParams } from './visualization/viz-url-params';

export function AssistantPanel() {
  const open = useAssistantStore(s => s.isOpen);
  const hasOpened = useAssistantStore(s => s.hasOpened);
  const openPanel = useAssistantStore(s => s.openPanel);
  const closePanel = useAssistantStore(s => s.closePanel);
  const { width, resizing, startResize } = usePanelResize();
  const jiraSupportEnabled = useJiraSupportEnabled();

  function handleOpen() {
    openPanel();
  }

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={handleOpen}
          className={cn(
            'fixed right-6 z-[10000] flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg ring-1 ring-black/5 transition-transform hover:scale-105',
            // Sits clear of the Intercom launcher until that widget is gone, then drops to
            // match the horizontal inset.
            jiraSupportEnabled ? 'bottom-6' : 'bottom-20'
          )}
          aria-label="Open Bravo Assistant">
          <FontAwesomeIcon icon={faSparkles} className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[9998] bg-black/40 transition-opacity"
          onClick={closePanel}
          aria-hidden
        />
      )}

      <aside
        style={{ width }}
        className={cn(
          'fixed bottom-0 right-0 top-0 z-[9999] flex h-lvh flex-col border-l border-border bg-background shadow-2xl',
          // Suppress the slide transition while dragging so width tracks the cursor 1:1.
          !resizing && 'transition-transform duration-300',
          open ? 'translate-x-0' : 'translate-x-full'
        )}>
        {open && (
          <div
            onMouseDown={startResize}
            className={cn(
              'absolute left-0 top-0 z-10 h-full w-1.5 -translate-x-1/2 cursor-col-resize transition-colors hover:bg-primary/40',
              resizing && 'bg-primary/40'
            )}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize assistant panel"
          />
        )}
        {hasOpened && <AssistantThread onClose={closePanel} />}
      </aside>
    </>
  );
}

/** Owns conversation selection: lands on the most-recent conversation, switches
 *  via the title dropdown, and remounts the chat (via `key`) for "New chat". */
function AssistantThread({ onClose }: { onClose: () => void }) {
  const utils = trpc.useUtils();
  const listQuery = trpc.assistantConversations.useQuery(undefined, {
    refetchOnWindowFocus: false
  });
  const launch = useAssistantStore(s => s.launch);
  const clearLaunch = useAssistantStore(s => s.clearLaunch);

  // `selectedId` is an explicitly chosen existing conversation (null → land on
  // the most-recent one); `newChatNonce` > 0 means the user started a fresh thread.
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [newChatNonce, setNewChatNonce] = React.useState(0);
  const resolvedLaunch = launch?.status === 'select' || launch?.status === 'send' ? launch : null;
  const launchConversationId = resolvedLaunch?.conversationId ?? null;
  // A 'send' launch carries its own seed and a server-reserved id, and the
  // reservation is still empty until the seed POSTs. Querying it would cache
  // `{ messages: [] }` and blank the chat on the next reopen, so it is kept out
  // of `activeId` (the single-conversation query stays disabled) and the chat is
  // keyed by the reserved id instead.
  const pendingSend = launch?.status === 'send' ? launch : null;
  const isNew = newChatNonce > 0 && !resolvedLaunch;

  const conversations = listQuery.data ?? [];
  const activeId =
    isNew || pendingSend
      ? null
      : (launchConversationId ?? selectedId ?? conversations[0]?.id ?? null);
  const resolvingLaunch = launch?.status === 'resolving';

  const conversationQuery = trpc.assistantConversation.useQuery(
    { id: activeId ?? '' },
    { enabled: !resolvingLaunch && !isNew && activeId != null, refetchOnWindowFocus: false }
  );

  const handleNewChat = React.useCallback(() => {
    if (launch) clearLaunch(launch.id);
    setSelectedId(null);
    setNewChatNonce(n => n + 1);
  }, [clearLaunch, launch]);

  const handleSelect = React.useCallback(
    (id: string) => {
      if (launch) clearLaunch(launch.id);
      setNewChatNonce(0);
      setSelectedId(id);
    },
    [clearLaunch, launch]
  );

  // Refresh the recency list so a new thread and its server-derived title appear.
  const handleTurnFinished = React.useCallback(() => {
    if (launch?.status === 'send') {
      setNewChatNonce(0);
      setSelectedId(launch.conversationId);
      clearLaunch(launch.id);
    }
    void utils.assistantConversations.invalidate();
  }, [clearLaunch, launch, utils.assistantConversations]);

  const renameMutation = trpc.assistantRenameConversation.useMutation({
    onSuccess: (_data, vars) => {
      void utils.assistantConversations.invalidate();
      // Also refresh the open conversation so the header title updates.
      void utils.assistantConversation.invalidate({ id: vars.id });
    }
  });
  const deleteMutation = trpc.assistantDeleteConversation.useMutation({
    onSuccess: () => void utils.assistantConversations.invalidate()
  });

  const handleRename = React.useCallback(
    (id: string, title: string) => renameMutation.mutate({ id, title }),
    [renameMutation]
  );

  const handleDelete = React.useCallback(
    (id: string) => {
      deleteMutation.mutate({ id });
      if (launchConversationId === id && launch) clearLaunch(launch.id);
      // If the active thread was the one deleted, fall back to the most-recent
      // remaining conversation (selectedId → null) instead of a stale/empty load.
      setSelectedId(prev => (prev === id ? null : prev));
    },
    [clearLaunch, deleteMutation, launch, launchConversationId]
  );

  if (
    resolvingLaunch ||
    listQuery.isLoading ||
    (!isNew && activeId != null && conversationQuery.isLoading)
  ) {
    return (
      <>
        <PanelHeader
          title="Bravo Assistant"
          conversations={conversations}
          activeId={activeId}
          onSelect={handleSelect}
          onRename={handleRename}
          onDelete={handleDelete}
          canNewChat={false}
          onClose={onClose}
        />
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          Loading…
        </div>
      </>
    );
  }

  const initialConversation = isNew || pendingSend ? null : (conversationQuery.data ?? null);
  // The send flow keys by its reserved id so the seed→resume handoff (and a rapid
  // re-click that re-seeds the same reservation) stays on one mount: the chat
  // never remounts and the `seedSentRef` guard still suppresses a duplicate seed.
  const resetKey = isNew
    ? `new-${newChatNonce}`
    : pendingSend
      ? pendingSend.conversationId
      : (initialConversation?.id ?? 'new');
  const title = initialConversation?.title ?? pendingSend?.seed.title ?? 'New conversation';

  return (
    <AssistantChat
      key={resetKey}
      title={title}
      pendingSend={pendingSend}
      conversationIdOverride={pendingSend?.conversationId}
      initialConversation={initialConversation}
      conversations={conversations}
      onSelect={handleSelect}
      onRename={handleRename}
      onDelete={handleDelete}
      onNewChat={handleNewChat}
      onTurnFinished={handleTurnFinished}
      onClose={onClose}
    />
  );
}

function AssistantChat({
  title,
  pendingSend,
  conversationIdOverride,
  initialConversation,
  conversations,
  onSelect,
  onRename,
  onDelete,
  onNewChat,
  onTurnFinished,
  onClose
}: {
  title: string;
  pendingSend?: PendingAssistantSend | null;
  conversationIdOverride?: string;
  initialConversation: AssistantConversation | null;
  conversations: ConversationSummary[];
  onSelect: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
  onNewChat: () => void;
  onTurnFinished: () => void;
  onClose: () => void;
}) {
  const activeId = initialConversation?.id ?? conversationIdOverride ?? null;
  const utils = trpc.useUtils();

  const endRef = React.useRef<HTMLDivElement | null>(null);
  const { messages, status, sendMessage } = useAssistantConversation(
    initialConversation,
    conversationIdOverride
  );

  // Page Context retargets as the user navigates; a dismissed chip reappears on
  // the next screen (dismissal is keyed to the route it was dismissed on).
  const pageContext = useCurrentPageContext();
  const contextLabel = usePageContextLabel(pageContext);
  const [dismissedRoute, setDismissedRoute] = React.useState<string | null>(null);
  const contextActive = hasPageContext(pageContext) && pageContext.route !== dismissedRoute;

  const [input, setInput] = React.useState('');
  const isBusy = status === 'submitted' || status === 'streaming';

  // Auto-send the seeded message once. `creation` lets the lazily-created
  // conversation skip the truncated-first-message title default. The guard keyed
  // by conversationId swallows replays on a long-mounted thread.
  const seedSentRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!pendingSend || seedSentRef.current === pendingSend.conversationId) return;
    const { seed } = pendingSend;
    seedSentRef.current = pendingSend.conversationId;
    void sendMessage(
      { text: seed.message, metadata: seed.pageContext },
      { body: { creation: { title: seed.title, origin: seed.origin } } }
    );
  }, [pendingSend, sendMessage]);

  React.useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, status]);

  // On turn finish, prime the single-conversation cache with the live transcript
  // so a later reopen — and the in-session send→resume handoff — reads the real
  // messages immediately. `useChat` only reads `messages` at mount, so the cache
  // must already be correct before that mount or the chat opens blank.
  const wasBusyRef = React.useRef(false);
  React.useEffect(() => {
    if (wasBusyRef.current && !isBusy) {
      if (activeId) {
        const origin = initialConversation?.origin ?? pendingSend?.seed.origin ?? null;
        utils.assistantConversation.setData({ id: activeId }, prev => ({
          id: activeId,
          title: prev?.title ?? title,
          origin: prev?.origin ?? origin,
          messages
        }));
      }
      onTurnFinished();
    }
    wasBusyRef.current = isBusy;
  }, [
    isBusy,
    onTurnFinished,
    activeId,
    messages,
    title,
    initialConversation,
    pendingSend,
    utils.assistantConversation
  ]);

  async function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || isBusy) return;
    setInput('');
    // Dropping the chip sends the turn with no context.
    await sendMessage({ text: trimmed, metadata: contextActive ? pageContext : undefined });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    void send(input);
  }

  return (
    <>
      <PanelHeader
        title={title}
        conversations={conversations}
        activeId={activeId}
        onSelect={onSelect}
        onRename={onRename}
        onDelete={onDelete}
        onNewChat={onNewChat}
        canNewChat={messages.length > 0 && !isBusy}
        onClose={onClose}
      />

      <div className="flex-1 overflow-y-auto px-5 py-5">
        <div
          className={cn(
            'mx-auto max-w-2xl space-y-4',
            messages.length === 0 && !isBusy && 'flex h-full flex-col items-center justify-center'
          )}>
          {messages.length === 0 && !isBusy ? (
            <ChatWelcome onSuggestionClick={prompt => void send(prompt)} disabled={isBusy} />
          ) : (
            messages.map(m => <MessageRow key={m.id} message={m} />)
          )}
          {isBusy && (
            <div className="flex justify-start">
              <div className="rounded-2xl bg-muted px-4 py-2 text-sm text-muted-foreground">
                Thinking...
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      <div className="shrink-0 border-t border-border px-5 py-3">
        {contextActive && (
          <div className="mx-auto mb-2 flex max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-1 text-xs text-muted-foreground">
              <FontAwesomeIcon icon={faLocationDot} className="h-3 w-3" />
              <span className="truncate">{contextLabel}</span>
              <button
                type="button"
                onClick={() => setDismissedRoute(pageContext.route)}
                className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full hover:bg-foreground/10"
                aria-label="Remove page context">
                <FontAwesomeIcon icon={faXmark} className="h-2.5 w-2.5" />
              </button>
            </span>
          </div>
        )}
        <form onSubmit={handleSubmit} className="mx-auto flex max-w-2xl items-end gap-2">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Ask Bravo anything…"
            disabled={isBusy}
            className="h-12 min-w-0 flex-1 rounded-xl border border-border bg-background px-3.5 text-sm shadow-sm outline-none placeholder:text-muted-foreground focus:border-primary/50 focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={isBusy || !input.trim()}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm transition-opacity disabled:opacity-40"
            aria-label="Send">
            <FontAwesomeIcon icon={faArrowUp} className="h-4 w-4" />
          </button>
        </form>
      </div>
    </>
  );
}

/** Names the focused entity instead of the URL's id-based label. Only campaigns
 *  resolve a name — `pageContext.id` is the zoho id the detail page already
 *  queries, so it's usually cached; line items and media buys have no id→name
 *  endpoint and fall back to their derived label (the raw id is never shown). */
function usePageContextLabel(pageContext: PageContext): string {
  const isCampaign = pageContext.entity === 'campaign';
  const campaignId = pageContext.id ?? '';
  const campaignQuery = trpc.campaignV2.useQuery(
    { zoho_campaign_id: campaignId },
    { enabled: isCampaign && campaignId !== '', staleTime: 5 * 60 * 1000 }
  );

  if (isCampaign && campaignQuery.data?.campaign_name) return campaignQuery.data.campaign_name;
  return pageContext.label;
}

function PanelHeader({
  title,
  conversations,
  activeId,
  onSelect,
  onRename,
  onDelete,
  onNewChat,
  canNewChat,
  onClose
}: {
  title: string;
  conversations: ConversationSummary[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
  onNewChat?: () => void;
  canNewChat: boolean;
  onClose: () => void;
}) {
  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
      <Menu as="div" className="relative min-w-0 flex-1">
        {({ close }) => (
          <>
            <MenuButton className="flex max-w-full items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-semibold hover:bg-muted">
              <span className="truncate">{title}</span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className="h-3 w-3 shrink-0 text-muted-foreground"
              />
            </MenuButton>
            {/* Rows are plain buttons (not MenuItems) so the rename input and
                per-row actions don't dismiss the menu; selecting closes it via
                the render-prop `close`. */}
            <MenuItems className="absolute left-0 z-10 mt-1 max-h-[60vh] w-80 overflow-y-auto rounded-md bg-background py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
              <button
                type="button"
                onClick={() => {
                  onNewChat?.();
                  close();
                }}
                disabled={!canNewChat}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted',
                  !canNewChat && 'opacity-40'
                )}>
                <FontAwesomeIcon icon={faPenToSquare} className="h-3.5 w-3.5" />
                New chat
              </button>
              {conversations.length > 0 && <div className="my-1 border-t border-border" />}
              {conversations.map(c => (
                <ConversationRow
                  key={c.id}
                  conversation={c}
                  isActive={c.id === activeId}
                  onSelect={id => {
                    onSelect(id);
                    close();
                  }}
                  onRename={onRename}
                  onDelete={onDelete}
                />
              ))}
            </MenuItems>
          </>
        )}
      </Menu>
      <button
        type="button"
        onClick={onClose}
        className="ml-auto flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
        aria-label="Close">
        <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
      </button>
    </header>
  );
}

function ConversationRow({
  conversation,
  isActive,
  onSelect,
  onRename,
  onDelete
}: {
  conversation: ConversationSummary;
  isActive: boolean;
  onSelect: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
}) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(conversation.title ?? '');

  function startEditing() {
    setDraft(conversation.title ?? '');
    setEditing(true);
  }

  function commit() {
    const next = draft.trim();
    if (next && next !== conversation.title) onRename(conversation.id, next);
    setEditing(false);
  }

  if (editing) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5">
        <input
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            // Keep keystrokes out of the Menu's type-ahead navigation.
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setEditing(false);
            }
          }}
          className="h-7 min-w-0 flex-1 rounded border border-border bg-background px-2 text-sm outline-none focus:border-primary/50"
          aria-label="Conversation title"
        />
      </div>
    );
  }

  return (
    <div className="group flex items-center gap-1 pr-2 hover:bg-muted">
      <button
        type="button"
        onClick={() => onSelect(conversation.id)}
        className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left text-sm">
        <FontAwesomeIcon
          icon={faCheck}
          className={cn('h-3 w-3 shrink-0', isActive ? 'opacity-100' : 'opacity-0')}
        />
        <span className="truncate">{conversation.title ?? 'New conversation'}</span>
      </button>
      <button
        type="button"
        onClick={startEditing}
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 hover:bg-foreground/10 group-hover:opacity-100"
        aria-label="Rename conversation">
        <FontAwesomeIcon icon={faPen} className="h-3 w-3" />
      </button>
      <button
        type="button"
        onClick={() => {
          if (window.confirm('Delete this conversation?')) onDelete(conversation.id);
        }}
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 hover:bg-foreground/10 group-hover:opacity-100"
        aria-label="Delete conversation">
        <FontAwesomeIcon icon={faTrashCan} className="h-3 w-3" />
      </button>
    </div>
  );
}

function MessageRow({ message }: { message: ChatMessage }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] space-y-2 rounded-2xl rounded-br-sm bg-primary px-3.5 py-2 text-sm text-primary-foreground shadow-sm">
          {message.parts.map((p, i) =>
            p.type === 'text' ? (
              <div key={i} className="whitespace-pre-wrap">
                {p.text}
              </div>
            ) : null
          )}
        </div>
      </div>
    );
  }

  const segments = buildMessageSegments(message.parts);

  return (
    <div className="space-y-2">
      {segments.map((seg, si) => {
        if (seg.type === 'text') {
          return (
            <div key={`text-${si}`} className="flex justify-start">
              <div className="prose prose-sm max-w-none rounded-2xl rounded-bl-sm bg-muted px-3.5 py-2 text-sm text-foreground shadow-sm">
                {seg.texts.map((t, ti) => (
                  <ReactMarkdown key={ti}>{t}</ReactMarkdown>
                ))}
              </div>
            </div>
          );
        }

        return (
          <div key={`viz-${si}`} className="w-full">
            <VisualizationBlock {...seg.props} vizUrl={vizUrlFor(seg.props.visualization)} />
          </div>
        );
      })}
    </div>
  );
}

function vizUrlFor(visualization: VisualizationBlockProps['visualization']): string | undefined {
  if (!visualization) return undefined;
  return `${window.location.origin}/viz?${encodeVizParams(visualization.query, visualization.labeledViz)}`;
}
